/**
 * Production Logging Service
 * Replaces console.log with proper logging that respects environment
 */

interface LogLevel {
  ERROR: 'error';
  WARN: 'warn';
  INFO: 'info';
  DEBUG: 'debug';
}

interface LogEntry {
  timestamp: string;
  level: keyof LogLevel;
  message: string;
  data?: unknown;
  userId?: string;
  organizationId?: string;
  sessionId?: string;
  platform?: string;
}

class ProductionLogger {
  private static instance: ProductionLogger;
  private sessionId: string;
  private userId?: string;
  private organizationId?: string;
  private logQueue: LogEntry[] = [];
  private readonly MAX_QUEUE_SIZE = 100;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.setupUnhandledErrorLogging();
  }

  static getInstance(): ProductionLogger {
    if (!ProductionLogger.instance) {
      ProductionLogger.instance = new ProductionLogger();
    }
    return ProductionLogger.instance;
  }

  /**
   * Set user context for all logs
   */
  setUserContext(userId: string, organizationId?: string): void {
    this.userId = userId;
    this.organizationId = organizationId;
  }

  /**
   * Log error messages (always logged)
   */
  error(message: string, data?: unknown): void {
    this.log('ERROR', message, data);
    
    // Also send to external error tracking (Sentry)
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(
        data instanceof Error ? data : new Error(message),
        {
          extra: data,
          user: {
            id: this.userId,
            organizationId: this.organizationId,
          },
          tags: {
            sessionId: this.sessionId,
          },
        }
      );
    }
  }

  /**
   * Log warning messages (always logged)
   */
  warn(message: string, data?: unknown): void {
    this.log('WARN', message, data);
  }

  /**
   * Log info messages (production + development)
   */
  info(message: string, data?: unknown): void {
    this.log('INFO', message, data);
  }

  /**
   * Log debug messages (development only)
   */
  debug(message: string, data?: unknown): void {
    if (__DEV__) {
      this.log('DEBUG', message, data);
    }
  }

  /**
   * Log business events (always logged)
   */
  business(event: string, data?: unknown): void {
    this.log('INFO', `[BUSINESS] ${event}`, data);
    
    // Send to analytics if available
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event.toLowerCase().replace(/\s+/g, '_'), {
        custom_parameter_1: data,
        user_id: this.userId,
        organization_id: this.organizationId,
      });
    }
  }

  /**
   * Log security events (always logged with high priority)
   */
  security(event: string, data?: unknown): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'ERROR',
      message: `[SECURITY] ${event}`,
      data,
      userId: this.userId,
      organizationId: this.organizationId,
      sessionId: this.sessionId,
      platform: this.getPlatform(),
    };

    // Always log security events
    this.logQueue.unshift(logEntry); // Add to front of queue
    
    if (__DEV__) {
      log.error(`🔒 [SECURITY] ${event}`, data);
    }

    // Send to external monitoring immediately
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureMessage(
        `Security Event: ${event}`,
        'error',
        {
          extra: data,
          user: {
            id: this.userId,
            organizationId: this.organizationId,
          },
          tags: {
            sessionId: this.sessionId,
            event_type: 'security',
          },
        }
      );
    }
  }

  /**
   * Log performance metrics
   */
  performance(metric: string, value: number, data?: unknown): void {
    this.log('INFO', `[PERFORMANCE] ${metric}: ${value}ms`, { ...data, value });
    
    // Send to performance monitoring if available
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'timing_complete', {
        name: metric,
        value: value,
        event_category: 'performance',
      });
    }
  }

  /**
   * Core logging method
   */
  private log(level: keyof LogLevel, message: string, data?: unknown): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data: this.sanitizeData(data),
      userId: this.userId,
      organizationId: this.organizationId,
      sessionId: this.sessionId,
      platform: this.getPlatform(),
    };

    // Add to queue
    this.logQueue.push(logEntry);
    
    // Maintain queue size
    if (this.logQueue.length > this.MAX_QUEUE_SIZE) {
      this.logQueue.shift();
    }

    // Output to console in development
    if (__DEV__) {
      const consoleMethod = this.getConsoleMethod(level);
      const prefix = this.getLogPrefix(level);
      
      if (data) {
        consoleMethod(`${prefix} ${message}`, data);
      } else {
        consoleMethod(`${prefix} ${message}`);
      }
    }

    // Send to external logging service in production
    if (!__DEV__ && this.shouldSendToExternalService(level)) {
      this.sendToExternalService(logEntry);
    }
  }

  /**
   * Get appropriate console method for log level
   */
  private getConsoleMethod(level: keyof LogLevel): Function {
    switch (level) {
      case 'ERROR': return console.error;
      case 'WARN': return console.warn;
      case 'INFO': return console.info;
      case 'DEBUG': return console.debug;
      default: return console.log;
    }
  }

  /**
   * Get log prefix with emoji for better visibility
   */
  private getLogPrefix(level: keyof LogLevel): string {
    switch (level) {
      case 'ERROR': return '🔴';
      case 'WARN': return '🟡';
      case 'INFO': return '🔵';
      case 'DEBUG': return '⚪';
      default: return '⚫';
    }
  }

  /**
   * Check if log should be sent to external service
   */
  private shouldSendToExternalService(level: keyof LogLevel): boolean {
    // In production, send ERROR and WARN levels
    return level === 'ERROR' || level === 'WARN';
  }

  /**
   * Send log to external service (implement based on your service)
   */
  private async sendToExternalService(logEntry: LogEntry): Promise<void> {
    try {
      // Example: Send to custom logging endpoint
      if (typeof fetch !== 'undefined') {
        await fetch('/api/logs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logEntry),
        });
      }
    } catch (error) {
      // Fail silently to avoid logging loops
      if (__DEV__) {
        log.error('Failed to send log to external service:', error);
      }
    }
  }

  /**
   * Sanitize data to remove sensitive information
   */
  private sanitizeData(data: unknown): unknown {
    if (!data) return data;
    
    if (typeof data === 'string') {
      return this.sanitizeString(data);
    }
    
    if (typeof data === 'object') {
      return this.sanitizeObject(data);
    }
    
    return data;
  }

  /**
   * Sanitize string data
   */
  private sanitizeString(str: string): string {
    // Remove potential API keys, tokens, passwords
    return str
      .replace(/([a-zA-Z0-9_-]{20,})/g, '[REDACTED_TOKEN]')
      .replace(/(password|token|key|secret)[\s:=]+[\S]+/gi, '$1=[REDACTED]')
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]');
  }

  /**
   * Sanitize object data
   */
  private sanitizeObject(obj: Record<string, unknown>): Record<string, unknown> {
    const sensitiveKeys = [
      'password', 'token', 'key', 'secret', 'apiKey', 'authToken',
      'accessToken', 'refreshToken', 'sessionToken', 'credential',
    ];

    const sanitized = { ...obj };
    
    for (const key in sanitized) {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive.toLowerCase()))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeObject(sanitized[key]);
      } else if (typeof sanitized[key] === 'string') {
        sanitized[key] = this.sanitizeString(sanitized[key]);
      }
    }
    
    return sanitized;
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current platform
   */
  private getPlatform(): string {
    if (typeof window !== 'undefined') {
      return 'web';
    }
    
    try {
      const { Platform } = require('react-native');
      return Platform.OS;
    } catch {
      return 'unknown';
    }
  }

  /**
   * Setup global error handlers
   */
  private setupUnhandledErrorLogging(): void {
    // Check if we're in a web environment with proper window support
    const isWebPlatform = this.getPlatform() === 'web';
    
    if (isWebPlatform && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      // Handle unhandled promise rejections
      window.addEventListener('unhandledrejection', (event) => {
        this.error('Unhandled Promise Rejection', {
          reason: event.reason,
          stack: event.reason?.stack,
        });
      });

      // Handle uncaught errors
      window.addEventListener('error', (event) => {
        this.error('Uncaught Error', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack,
        });
      });
    } else {
      // For React Native platforms, we don't set up window event listeners
      // React Native has its own error handling mechanisms
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('ProductionLogger: Skipping web-specific error handlers on native platform');
      }
    }
  }

  /**
   * Get current log queue (for debugging)
   */
  getLogQueue(): LogEntry[] {
    return [...this.logQueue];
  }

  /**
   * Clear log queue
   */
  clearLogQueue(): void {
    this.logQueue = [];
  }
}

// Create singleton instance
const logger = ProductionLogger.getInstance();

// Export convenient logging functions
export const log = {
  error: (message: string, data?: unknown) => logger.error(message, data),
  warn: (message: string, data?: unknown) => logger.warn(message, data),
  info: (message: string, data?: unknown) => logger.info(message, data),
  debug: (message: string, data?: unknown) => logger.debug(message, data),
  business: (event: string, data?: unknown) => logger.business(event, data),
  security: (event: string, data?: unknown) => logger.security(event, data),
  performance: (metric: string, value: number, data?: unknown) => logger.performance(metric, value, data),
  setUserContext: (userId: string, organizationId?: string) => logger.setUserContext(userId, organizationId),
};

export default logger;